
import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import ProvideUpcomingSportEvents from '@/components/sports/single-step/ProvideUpcomingSportEvents.vue';

import AdUnit from '@/components/AdUnit.vue';
import EventBackdrop from '@/components/sports/common/event/EventBackdrop.vue';
import EventBanner from '@/components/sports/common/event/EventBanner.vue';
import EventBuybox from '@/components/sports/common/event/EventBuybox.vue';
import EventOverview from '@/components/sports/common/event/EventOverview.vue';
import EventSpinningText from '@/components/sports/common/event/EventSpinningText.vue';
import SingleStepEventDetails from '@/components/sports/common/event/SingleStepEventDetails.vue';
import SingleStepEventLineup from '@/components/sports/common/event/SingleStepEventLineup.vue';
import SingleStepEventEncounterList from '@/components/sports/common/SingleStepEventEncounterList.vue';
import SportsLayout from '@/components/sports/common/SportsLayout.vue';

import { shiftHours } from '@/helpers/date-helper';
import { Locale } from '@/helpers/locale-helper';
import { ModalHelper } from '@/helpers/modal-helper';
import { trackClickOutSports } from '@/helpers/offer-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { contextSportIs, getSportPageContext, SingleStepEventContext } from '@/helpers/sports/config';
import { formatDate, formatTime } from '@/helpers/time-zone-helper';
import { SnowplowSportContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import {
	Platform,
	SingleStepSportEvent,
	SportEventStatus,
	SportOffer,
	SportType,
	SraPlacement,
} from '@/@types/graphql-types';
import { GetSportEventV2Document, GetSportEventV2QueryVariables } from '@/pages/graphql/queries/GetSportEventV2.query';

import { AppDirection } from '@/enums/app-direction';
import { isMultiStepSport, SportLocale } from '@/enums/sports';

import { toastError } from '@/helpers/toast/toast-helper';
import { SportEventV2Url, SportPageType } from '@/interfaces/sport-graphql';

const StreamLanguageSwitcher = () => import('@/components/sports/common/StreamLanguageSwitcher.vue');

const Language = namespace('language');
const Meta = namespace('meta');
const User = namespace('user');

@Component({
	name: 'SingleStepEventPage',
	components: {
		AdUnit,
		EventBackdrop,
		EventBanner,
		EventBuybox,
		SingleStepEventEncounterList,
		EventOverview,
		EventSpinningText,
		ProvideUpcomingSportEvents,
		SingleStepEventDetails,
		SingleStepEventLineup,
		SportsLayout,
		StreamLanguageSwitcher,
	},
	apollo: {
		eventData: {
			fetchPolicy: 'cache-and-network',
			query: GetSportEventV2Document,
			variables(): GetSportEventV2QueryVariables {
				return this.queryVariables;
			},
			update(data) {
				return data.urlV2;
			},
			skip() {
				if (this.$route.name != null && this.$route.name.includes('event')) return false;
				return true;
			},
			result(_result, _key) {
				this.SET_JSON_LD(this.schemaData);
			},
			errorPolicy: 'all',
			error(error) {
				captureMessageForSentry(
					'[GraphQL Get Single Step Event Data]:',
					{ error: error, where: 'Component: SingleStepEventPage' },
					'error'
				);
				process.client ? this.showErrorToast() : null;
			},
		},
	},
	metaInfo() {
		return this.head;
	},
})
export default class SingleStepEventPage extends Vue {
	eventData: SportEventV2Url<SingleStepSportEvent>;

	@Language.Getter country: string;
	@Language.Getter language: string;
	@Language.Getter locale: string;
	@Language.State locales: Locale[];
	@Language.State webLocale: SportLocale;

	@Meta.Getter appDirection: AppDirection;
	@Meta.Mutation SET_JSON_LD: (content: string) => void;

	@User.State timeZone: string | undefined;

	context = {} as SingleStepEventContext;

	@Watch('$route.meta.sport', { immediate: true })
	setContext(newMeta: SportType | undefined) {
		if (newMeta && newMeta !== this.context.sportType && !isMultiStepSport(newMeta)) {
			this.context = getSportPageContext({
				sport: this.$route.meta!.sport as SportType,
				pageType: SportPageType.SportEvent,
			});
		}
	}

	basketBallTempStageFix() {
		const isNBA = this.event.competition?.id === 'scn-bkb-8331232' ? true : false;
		if (isNBA) return;
		return `(${this.stage})`;
	}

	@Watch('pageIdentity', { immediate: true })
	onTitleDetailChange(newIdentity: string, oldIdentity: string | undefined) {
		if (newIdentity && newIdentity !== oldIdentity) {
			// note: all sport pages are excluded from automatic page tracking
			TrackingHelper.trackPage(this.$route.path, [this.sportContext]);
		}
	}

	get SraPlacement() {
		return SraPlacement;
	}

	// in case the user switches the country of the same event, we need to listen to changes to locale changes as well
	get pageIdentity() {
		return [this.event.id, this.eventData?.locale].filter(Boolean).join('__');
	}

	get queryVariables(): GetSportEventV2QueryVariables {
		// cut out query parameters that make gql queries hiccups
		const fullPath = this.$route.fullPath.toString().split('?')[0];
		return {
			language: this.language,
			country: this.country,
			platform: Platform.Web,
			fullPath,
		};
	}

	get event() {
		return this.eventData?.node ?? {};
	}

	get competitionId() {
		return this.event.competition?.objectId;
	}

	get eventStatus() {
		return this.event?.content?.status;
	}

	get availableStreamLanguages() {
		if (!this.shouldRenderStreamLanguageSwitcher) return [];

		const languages = this.event.offers.map(offer => offer.audioLanguage);

		return [...new Set(languages)];
	}

	// is the title detail page in loading mode for:
	// - requests
	get isDataLoading() {
		return this.$apollo.queries.eventData.loading && this.event != null;
	}

	get head() {
		return {};
	}

	get schemaData() {
		const seasonName = this.event.content?.season?.name ?? '';
		const description = `${this.eventTitle} ${this.competitionName} ${seasonName}` ?? '';
		const sportName = this.$t(this.context.sportName);
		let eventStatus = '';
		switch (this.event.content?.status) {
			case SportEventStatus.Postponed:
				eventStatus = 'EventPostponed';
				break;
			case SportEventStatus.Cancelled:
				eventStatus = 'EventCancelled';
				break;
			default:
				eventStatus = 'EventScheduled';
		}

		const endDate = shiftHours(this.event.content?.startAt, 2)?.toISOString();

		const prices =
			this.event?.offers?.map((offer: any) => offer.retailPrice as number).filter((v: any) => v != null) ?? [];
		const schemaDataObject = {
			'@context': 'http://schema.org',
			'@type': 'BroadcastEvent',
			name: description,
			description,
			isLiveBroadcast: (this.event?.offers ?? []).length > 0,
			startDate: this.event.content?.startAt,
			endDate,
			organizer: {
				'@type': 'Organization',
				name: this.competitionName,
			},
			eventAttendanceMode: 'OnlineEventAttendanceMode',
			broadcastOfEvent: {
				'@type': 'SportsEvent',
				sport: sportName,
				organizer: {
					'@type': 'Organization',
					name: this.competitionName,
				},
				eventStatus,
				name: description,
				description,
				awayTeam: {
					'@type': 'SportsTeam',
					name: this.event?.competitors?.awayTeam?.competitors.map(c => c.content.name).find(Boolean) ?? '',
					sport: sportName,
				},
				homeTeam: {
					'@type': 'SportsTeam',
					name: this.event?.competitors?.homeTeam?.competitors.map(c => c.content.name).find(Boolean) ?? '',
					sport: sportName,
				},
				startDate: this.event.content?.startAt,
				endDate,
				location: {
					'@type': 'SportsActivityLocation',
					name: this.event.content?.venue?.name,
				},
				offers: this.event?.offers
					? {
							'@type': 'AggregateOffer',
							highPrice: Math.max(...prices) ?? undefined,
							lowPrice: Math.min(...prices) ?? undefined,
							priceCurrency: this.event.offers.map((offer: any) => offer.currency).find(Boolean),
							offerCount: this.event.offers.length,
							offers: this.event.offers.map((offer: any) => ({
								'@type': 'Offer',
								name: `${description} ${offer.presentationType} ${offer.monetizationType}`,
								price: offer.retailPrice,
								priceCurrency: offer.currency,
								url: offer.standardWebURL,
							})),
					  }
					: null,
				audience: {
					'@type': 'Audience',
					name: 'Sports Fans',
				},
			},
		};

		return JSON.parse(JSON.stringify(schemaDataObject).replace(/&quot;/g, '"')) || '';
	}

	get hasRelevantOffers() {
		return (this.event?.offers ?? []).length > 0;
	}

	get eventTitle() {
		const { key, params } = this.context.getEventTitle(this.event, this.webLocale);
		return this.$t(key, params);
	}

	get competitionName() {
		return this.event?.competition?.content.name;
	}

	get isNBA(): boolean {
		return this.event.competition?.id === 'scn-bkb-8331232';
	}

	get isNFL(): boolean {
		return this.event.competition?.id === 'scn-afb-7440571';
	}

	get isMLB(): boolean {
		return this.event.competition?.id === 'scn-bsb-11678542';
	}

	get isNHL(): boolean {
		return this.event.competition?.id === 'scn-ice-17698463';
	}

	get stage() {
		const now = new Date().getTime();

		if (this.isNBA) {
			if (now <= new Date('2024-04-14').setUTCHours(23, 59, 59, 999))
				return this.$tc('SPORTS_WEBAPP_REGULAR_SEASON');
		}

		if (
			this.isNFL &&
			![
				'AMERICAN_FOOTBALL_CONFERENCE_CHAMPIONSHIP',
				'AMERICAN_FOOTBALL_CONFERENCE_DIVISIONAL_ROUND',
				'AMERICAN_FOOTBALL_CONFERENCE_WILD_CARD_ROUND',
				'NATIONAL_FOOTBALL_CONFERENCE_CHAMPIONSHIP',
				'NATIONAL_FOOTBALL_CONFERENCE_DIVISIONAL_ROUND',
				'NATIONAL_FOOTBALL_CONFERENCE_WILD_CARD_ROUND',
			].includes(this.event.content.competitionStage?.name as string)
		) {
			if (now <= new Date('2024-01-12').setUTCHours(23, 59, 59, 999))
				return this.$tc('SPORTS_WEBAPP_REGULAR_SEASON');
		}

		if (this.isMLB) {
			if (now <= new Date('2023-10-01').setUTCHours(23, 59, 59, 999))
				return this.$tc('SPORTS_WEBAPP_REGULAR_SEASON');
		}

		if (this.isNHL) {
			if (now <= new Date('2024-04-18').setUTCHours(23, 59, 59, 999))
				return this.$tc('SPORTS_WEBAPP_REGULAR_SEASON');
		}

		return this.context.getStageHeading(this.event.content.competitionStage, this.$t.bind(this));
	}

	get eventStartAt() {
		return this.event.content?.startAt == null ? null : new Date(this.event.content.startAt);
	}

	get kickoffDate() {
		if (this.eventStartAt == null) return undefined;
		return formatDate(this.eventStartAt, this.locales, this.webLocale, this.timeZone, this.language);
	}

	get kickoffTime() {
		if (this.eventStartAt == null) return undefined;
		return formatTime(this.eventStartAt, this.locales, this.webLocale, this.timeZone, this.language);
	}

	get spinningTextProps() {
		return {
			event: this.event,
			eventTitle: this.eventTitle,
			status: this.eventStatus,
			date: this.kickoffDate,
			time: this.kickoffTime,
		};
	}

	get sportContext() {
		return SnowplowSportContext.fromSportEventV2(this.context.sportType, this.context.pageType, this.event);
	}

	get shouldRenderStreamLanguageSwitcher() {
		return contextSportIs.esports(this.context) && this.context.meta.showStreamSwitcher;
	}

	get isNotificationActive() {
		return !!this.event.mysportlistEntry?.createdAt;
	}

	handleClickout(
		offerLocation: 'backdrop' | 'buybox' | 'watchnow',
		offer: { offer: SportOffer; index: number | undefined }
	) {
		trackClickOutSports(offer.offer, offer.index, 0, offer.offer.sportObjectType, offerLocation, [
			this.sportContext,
		]);
	}

	async openCountrySwitcherModal() {
		const { default: CountrySwitcherModal } = await import(
			'@/components/buybox/country-switcher/CountrySwitcherModal.vue'
		);
		ModalHelper.openModal(CountrySwitcherModal, { closable: true, additionalContexts: [this.sportContext] });
		TrackingHelper.trackEvent('userinteraction', { action: 'country_selector_clicked' }, [this.sportContext]);
	}

	showErrorToast() {
		toastError({ message: this.$t('WEBAPP_ALERT_ERROR_TITLE') as string, duration: 2000 });
	}
}
